import { getFeedbackLabelsToDisplay } from '@modules/feedback/components/FeedbackCard/utils';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { TrackedKeywordPrediction } from '@modules/feedback/store/types';
import {
  getOptimisticEntityUnits,
  getTrackedKeywordPredictions,
} from '@modules/feedback/utils';
import { ManipulationType } from 'generated/graphql';
import { useMemo } from 'react';
import { FeedbackLabel, FeedbackLabelType } from 'v2/core/entities/feedback';
import { EntityLabel } from 'v2/core/entities/feedback/labels';
import { UUID } from 'v2/core/entities/uuid';

const EMPTY_SUGGESTIONS: EntityLabel[] = [];

interface useGetFeedbackTrackedKeywordLabelsOptions {
  labels: EntityLabel[];
  feedbackID: UUID;
  selectedLabels?: FeedbackLabel[];
  shouldSortBySelection?: boolean;
}

export const useGetFeedbackTrackedKeywordLabels = ({
  labels,
  feedbackID,
  selectedLabels = [],
  shouldSortBySelection = false,
}: useGetFeedbackTrackedKeywordLabelsOptions) => {
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const editedRecord = editedRecords.find(
    (editedRecord) => editedRecord.id === feedbackID,
  );

  const editedTrackedKeywordPredictions = useMemo(
    () => getTrackedKeywordPredictions(editedRecord),
    [editedRecord],
  );

  const feedbackTrackedKeywordLabels = getFeedbackLabelsToDisplay({
    labels,
    selectedLabels,
    shouldSortBySelection,
  });

  const filteredRemovedTrackedKeywordLabels = useMemo(
    () =>
      feedbackTrackedKeywordLabels.filter((label): label is EntityLabel => {
        if (label.type === FeedbackLabelType.Entity) {
          return !editedTrackedKeywordPredictions?.find(
            (editedTrackedKeywordPrediction) =>
              editedTrackedKeywordPrediction.prediction.id === label.id,
          );
        }
        return false;
      }),
    [editedTrackedKeywordPredictions, feedbackTrackedKeywordLabels],
  );

  const alreadyAddedTrackedKeywordLabels = useMemo(
    () =>
      editedTrackedKeywordPredictions
        ?.filter(
          (editPrediction) =>
            editPrediction.manipulationType === ManipulationType.Add,
        )
        .map((trackedKeywordPrediction) => ({
          ...trackedKeywordPrediction.prediction,
          ...(trackedKeywordPrediction.category
            ? {
                entityUnits: getOptimisticEntityUnits(
                  trackedKeywordPrediction.category,
                ),
              }
            : {}),
        })) ?? EMPTY_SUGGESTIONS,
    [editedTrackedKeywordPredictions],
  );

  const currentlyAddingTrackedKeywordLabels = useMemo(
    () =>
      recordInEditMode?.editPredictions
        ?.filter(
          (editPrediction): editPrediction is TrackedKeywordPrediction =>
            editPrediction.type === FeedbackLabelType.Entity &&
            editPrediction.manipulationType === ManipulationType.Add,
        )
        .map((trackedKeywordPrediction) => ({
          ...trackedKeywordPrediction.prediction,
          ...(trackedKeywordPrediction.category
            ? {
                entityUnits: getOptimisticEntityUnits(
                  trackedKeywordPrediction.category,
                ),
              }
            : {}),
        })) ?? EMPTY_SUGGESTIONS,
    [recordInEditMode?.editPredictions],
  );

  return useMemo(
    () =>
      Array.from(
        new Set([
          ...filteredRemovedTrackedKeywordLabels,
          ...(editedRecord?.id === feedbackID
            ? alreadyAddedTrackedKeywordLabels
            : EMPTY_SUGGESTIONS),
          ...(recordInEditMode?.id === feedbackID
            ? currentlyAddingTrackedKeywordLabels
            : EMPTY_SUGGESTIONS),
        ]),
      ),
    [
      alreadyAddedTrackedKeywordLabels,
      currentlyAddingTrackedKeywordLabels,
      editedRecord?.id,
      feedbackID,
      filteredRemovedTrackedKeywordLabels,
      recordInEditMode?.id,
    ],
  );
};
